import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { chunkArray } from '../../utils';

const ns = `product-filter-bar`;

const filterTabs = ['Health Interest', 'Life Stage', 'Dietary Preference', 'Format'];

const ProductFilterBar = ({
	page,
	products,
	handleFilteredProducts,
}) => {
	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }__${ page }`]: page,
	});

	const [pageLoaded, setPageLoaded] = useState(false);
	const [sortKey, setSortKey] = useState('Default');
	const [activeFilters, setActiveFilters] = useState([]);
	const [activeFilterDropdown, setActiveFilterDropdown] = useState('');

	const filterActive = activeFilters.length > 0;

	const stripDoubleSemicolon = string => {
		if (string.includes('::')) {
			return string.split('::')[1];
		}
		return string;
	};

	const getProductPrice = (product) => {
		const priceRange = product.priceRangeV2?.minVariantPrice.amount || product.priceRange?.minVariantPrice.amount;
		return parseFloat(priceRange);
	};

	const sortProducts = (a, b) => {
		switch (sortKey) {
			case 'Newest':
				return new Date(b.createdAt) - new Date(a.createdAt);
			case 'A-Z':
				return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
			case 'Price: High-Low':
				return getProductPrice(b) - getProductPrice(a);
			case 'Price: Low-High':
			default:
				return getProductPrice(a) - getProductPrice(b);
		}
	};

	const onClickTabItem = tab => {
		if (activeFilterDropdown === tab) {
			setActiveFilterDropdown('');
		} else {
			setActiveFilterDropdown(tab);
		}
	};

	const handleResetState = () => {
		setActiveFilters([]);

		handleFilteredProducts([...products]);
	};

	const handleRemoveFilterOption = text => {
		setActiveFilters(prevState => {
			const index = prevState.indexOf(text);

			// If index === 0 this means that it's in the orignal
			// state of having none of the options selected so we can
			/// set the filteredProducts to 'All of the products'
			if (index === 0) {
				handleFilteredProducts([...products]);
			}

			prevState.splice(index, 1);
			return [...prevState];
		});
	};

	const optionView = text => {
		return (
			<button
				key={text}
				className={`${ ns }__filter-option`}
				onClick={() => {
					if (!activeFilters.includes(text)) {
						setActiveFilters(prevState => {
							return [
								...prevState,
								text,
							];
						});
					} else {
						handleRemoveFilterOption(text);
					}
				}}
			>
				{activeFilters.includes(text) ? (
					<svg width={'12'} height={'12'} viewBox={'0 0 12 12'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
						<rect x={'0.5'} y={'0.5'} width={'11'} height={'11'} rx={'2.5'} stroke={'#003C77'} />
						<rect x={'3.5'} y={'3.5'} width={'5'} height={'5'} rx={'0.5'} fill={'#003C77'} stroke={'#003C77'} />
					</svg>
				) : (
					<svg width={'12'} height={'12'} viewBox={'0 0 12 12'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
						<rect x={'0.5'} y={'0.5'} width={'11'} height={'11'} rx={'2.5'} stroke={'#BFCEDD'} />
					</svg>
				)}
				{stripDoubleSemicolon(text)}
			</button>
		);
	};

	const clearView = text => {
		return (
			<button
				key={text}
				className={`${ ns }__clear-single`}
				onClick={() => { handleRemoveFilterOption(text); }}
			>
				<svg width={'6'} height={'7'} viewBox={'0 0 6 7'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
					<path fillRule={'evenodd'} clipRule={'evenodd'} d={'M0.396418 5.39712C0.201172 5.5924 0.201197 5.90898 0.396475 6.10423C0.591753 6.29947 0.908336 6.29945 1.10358 6.10417L2.64706 4.56044C2.84232 4.36515 3.15891 4.36513 3.35419 4.56039L4.89789 6.10397C5.09316 6.29922 5.40974 6.29921 5.60499 6.10394C5.80025 5.90867 5.80024 5.59209 5.60497 5.39683L4.06121 3.8532C3.86594 3.65795 3.86592 3.34138 4.06116 3.14611L5.60447 1.60255C5.79972 1.40727 5.79969 1.09069 5.60441 0.895441C5.40914 0.700195 5.09255 0.700221 4.89731 0.895499L3.35406 2.439C3.1588 2.63428 2.84221 2.6343 2.64694 2.43904L1.10365 0.895886C0.908382 0.700632 0.591799 0.700645 0.396545 0.895915C0.201291 1.09118 0.201303 1.40777 0.396574 1.60302L1.93991 3.14624C2.13518 3.34149 2.1352 3.65806 1.93996 3.85333L0.396418 5.39712Z'} fill={'currentColor'} />
				</svg>
				{stripDoubleSemicolon(text)}
			</button>
		);
	};

	const tabContentView = filterText => {
		const filterOptions = [];

		products.map(product => {
			product.tags.length && product.tags.map(tag => {
				if (tag.includes(filterText)) {
					return filterOptions.push(tag);
				}
				return null;
			});
			return null;
		});

		const uniqueFilterOptions = [...new Set(filterOptions)];

		return chunkArray(uniqueFilterOptions, 5).map((options, i) => {
			return (
				// eslint-disable-next-line react/no-array-index-key
				<div className={`${ ns }__filter-tab-content-column`} key={i}>
					{options.map(option => {
						return optionView(option);
					})}
				</div>
			);
		});
	};

	useEffect(() => {
		const filteredProducts = [];

		if (pageLoaded) {
			products.map(product => {
				const searchProduct = !!product.variants?.edges;
				const collections = searchProduct
					? [product.collections.edges[0].node]
					: product.collections;

				if (activeFilters.find(tag => {
					const isProductTagged = product.tags.includes(tag);
					const collectionTitles = Array.isArray(collections) ? collections.map(collection=>collection.title) : [];	//	eslint-disable-line arrow-spacing
					const isProductInCollectionMatchingTag = collectionTitles.includes(stripDoubleSemicolon(tag));

					return isProductTagged || isProductInCollectionMatchingTag;
				})) {
					filteredProducts.push(product);
				}
				return null;
			});

			if (filteredProducts.length === 0) {
				filteredProducts.push(...products);
			}

			if (sortKey !== 'Default' && sortKey !== 'Featured') {
				filteredProducts.sort(sortProducts);
			}

			handleFilteredProducts([...filteredProducts]);
		}
	}, [activeFilters, sortKey]); // eslint-disable-line

	useEffect(() => {
		setPageLoaded(true);
	}, []);

	return (
		<div className={`${ rootClassnames }`}>
			{filterActive && (
				<div className={`${ ns }__clear`}>
					<button className={`${ ns }__clear-all`} onClick={() => { handleResetState(); }}>
						Clear Filter
					</button>
					<div className={`${ ns }__clear-singles`}>
						{activeFilters.map(option => {
							return clearView(option, 'category');
						})}
					</div>
				</div>
			)}
			<div className={`${ ns }__filter`}>
				<div className={`${ ns }__filter-tab-list-container`}>
					<div className={`${ ns }__filter-tab-list`}>
						{filterTabs.map(filterTab => {
							return (
								<button
									key={filterTab}
									className={`${ ns }__filter-tab ${ activeFilterDropdown === filterTab ? 'active' : '' }`}
									onClick={() => { onClickTabItem(filterTab); }}
								>
									{filterTab}
									<svg width={'8'} height={'5'} viewBox={'0 0 8 5'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
										<path d={'M7.53542 0.481192C7.2737 0.216296 6.84606 0.216063 6.58406 0.480673L4.7106 2.37276C4.31929 2.76796 3.68071 2.76796 3.2894 2.37276L1.41594 0.480673C1.15394 0.216063 0.726299 0.216296 0.464582 0.481192C0.207098 0.741803 0.207098 1.16103 0.464582 1.42164L3.28864 4.28C3.68012 4.67623 4.31988 4.67623 4.71136 4.28L7.53542 1.42164C7.7929 1.16103 7.7929 0.741803 7.53542 0.481192Z'} fill={'currentColor'} />
									</svg>
								</button>
							);
						})}
					</div>
				</div>
				<div className={`${ ns }__filter-tab-content-container`}>
					{activeFilterDropdown === 'Dietary Preference' && (
						<div className={`${ ns }__filter-tab-content dietary-preference`}>
							{tabContentView('Dietary Preference::')}
						</div>
					)}
					{activeFilterDropdown === 'Health Interest' && (
						<div className={`${ ns }__filter-tab-content health-interest`}>
							{tabContentView('Health Interest::')}
						</div>
					)}
					{activeFilterDropdown === 'Life Stage' && (
						<div className={`${ ns }__filter-tab-content life-stage`}>
							{tabContentView('Life Stage::')}
						</div>
					)}
					{activeFilterDropdown === 'Format' && (
						<div className={`${ ns }__filter-tab-content format`}>
							{tabContentView('Format::')}
						</div>
					)}
				</div>
			</div>
			<div className={`${ ns }__sort ${ sortKey === 'Default' ? 'default' : '' }`}>
				<div className={`${ ns }__sort-title eyebrow`}>
					{sortKey !== 'Default' ? sortKey : 'Sort By'}
					<svg width={'8'} height={'5'} viewBox={'0 0 8 5'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
						<path d={'M7.53542 0.481192C7.2737 0.216296 6.84606 0.216063 6.58406 0.480673L4.7106 2.37276C4.31929 2.76796 3.68071 2.76796 3.2894 2.37276L1.41594 0.480673C1.15394 0.216063 0.726299 0.216296 0.464582 0.481192C0.207098 0.741803 0.207098 1.16103 0.464582 1.42164L3.28864 4.28C3.68012 4.67623 4.31988 4.67623 4.71136 4.28L7.53542 1.42164C7.7929 1.16103 7.7929 0.741803 7.53542 0.481192Z'} fill={'#809DBB'} />
					</svg>
				</div>
				<select
					value={sortKey}
					// eslint-disable-next-line
                	onChange={(e) => setSortKey(e.target.value)}
				>
					<option value={'Default'} style={{display: 'none'}}>Default</option>
					<option value={'Featured'}>Featured</option>
					<option value={'A-Z'}>A-Z</option>
					<option value={'Newest'}>Newest</option>
					<option value={'Price: High-Low'}>Price: High-Low</option>
					<option value={'Price: Low-High'}>Price: Low-High</option>
				</select>
			</div>
		</div>
	);
};

export default ProductFilterBar;
